// src/context/UserContext.js
import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';

const UserContext = createContext();

// Token management utilities
const TokenService = {
  getAccessToken: () => localStorage.getItem('access_token'),
  getRefreshToken: () => localStorage.getItem('refresh_token'),
  setTokens: (access, refresh) => {
    localStorage.setItem('access_token', access);
    localStorage.setItem('refresh_token', refresh);
  },
  clearTokens: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  },
  isTokenExpired: (token) => {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  }
};

// Authenticated fetch utility
const authFetch = async (url, options = {}) => {
  let accessToken = TokenService.getAccessToken();

  // If token is expired, try to refresh it
  if (TokenService.isTokenExpired(accessToken)) {
    const refreshToken = TokenService.getRefreshToken();
    if (refreshToken && !TokenService.isTokenExpired(refreshToken)) {
      try {
        const response = await fetch('/api/auth/refresh/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh: refreshToken }),
        });
        if (response.ok) {
          const data = await response.json();
          localStorage.setItem('access_token', data.access);
          accessToken = data.access;
        } else {
          TokenService.clearTokens();
          return null;
        }
      } catch {
        TokenService.clearTokens();
        return null;
      }
    } else {
      TokenService.clearTokens();
      return null;
    }
  }

  const headers = {
    ...options.headers,
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  return fetch(url, { ...options, headers });
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const initialized = useRef(false);

  // Fetch full user profile
  const fetchUserProfile = useCallback(async () => {
    try {
      // First try session-based auth with full user data
      const response = await fetch('/api/auth-status/', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.isAuthenticated && data.user) {
          return {
            isAuthenticated: true,
            id: data.user.id,
            username: data.user.username,
            email: data.user.email,
            firstName: data.user.firstName || data.user.first_name || '',
            lastName: data.user.lastName || data.user.last_name || '',
            display_name: data.user.display_name || data.user.firstName || data.user.username || data.user.email,
            profile_picture: data.user.profile_picture || data.user.avatar,
            ...data.user,
          };
        }
      }
    } catch (e) {
      console.error('Error fetching user profile:', e);
    }
    return null;
  }, []);

  const checkAuthStatus = useCallback(async () => {
    const accessToken = TokenService.getAccessToken();

    // If we have a valid JWT token, we're authenticated
    if (accessToken && !TokenService.isTokenExpired(accessToken)) {
      try {
        // Decode token to get basic user info
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        // Fetch full profile
        const profile = await fetchUserProfile();
        if (profile) {
          return profile;
        }
        // Fallback to basic JWT data
        return { isAuthenticated: true, id: payload.user_id, username: payload.username };
      } catch {
        // Fall through to session check
      }
    }

    // Try to refresh token
    const refreshToken = TokenService.getRefreshToken();
    if (refreshToken && !TokenService.isTokenExpired(refreshToken)) {
      try {
        const response = await fetch('/api/auth/refresh/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh: refreshToken }),
        });
        if (response.ok) {
          const data = await response.json();
          localStorage.setItem('access_token', data.access);
          // Fetch full profile after token refresh
          const profile = await fetchUserProfile();
          if (profile) {
            return profile;
          }
          const payload = JSON.parse(atob(data.access.split('.')[1]));
          return { isAuthenticated: true, id: payload.user_id, username: payload.username };
        }
      } catch {
        // Fall through to session check
      }
    }

    // Fall back to session-based auth check (for backwards compatibility)
    const profile = await fetchUserProfile();
    if (profile) {
      return profile;
    }

    return { isAuthenticated: false };
  }, [fetchUserProfile]);

  const login = useCallback(async (email, password) => {
    const response = await fetch('/api/auth/login/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (response.ok) {
      if (data.requires_2fa) {
        return { requires_2fa: true, temp_token: data.temp_token, method: data.method };
      }

      // Store tokens
      TokenService.setTokens(data.access, data.refresh);
      setUser({ isAuthenticated: true, ...data.user });
      return { success: true, user: data.user };
    }

    return { success: false, error: data.error || data.detail || 'Login failed' };
  }, []);

  const verify2FA = useCallback(async (tempToken, code) => {
    const response = await fetch('/api/auth/verify-2fa/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ temp_token: tempToken, code }),
    });

    const data = await response.json();

    if (response.ok) {
      TokenService.setTokens(data.access, data.refresh);
      setUser({ isAuthenticated: true, ...data.user });
      return { success: true, user: data.user };
    }

    return { success: false, error: data.error || data.detail || 'Verification failed' };
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = TokenService.getRefreshToken();

    try {
      // Attempt to blacklist the refresh token
      await fetch('/api/auth/logout/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: refreshToken }),
      });
    } catch {
      // Ignore errors during logout
    }

    // Clear tokens and user state
    TokenService.clearTokens();
    setUser({ isAuthenticated: false });

    // Also clear any session cookies
    try {
      await fetch('/api/logout/', { method: 'POST', credentials: 'include' });
    } catch {
      // Ignore errors
    }
  }, []);

  useEffect(() => {
    // Only run once on mount
    if (initialized.current) return;
    initialized.current = true;

    const initializeApp = async () => {
      try {
        // Fetch auth status and data in parallel
        const [authStatus, categoriesResponse, citiesResponse] = await Promise.all([
          checkAuthStatus(),
          fetch('/api/categories/'),
          fetch('/api/cities/')
        ]);

        setUser(authStatus);

        const categoriesData = await categoriesResponse.json();
        const citiesData = await citiesResponse.json();
        // Sort categories alphabetically by name, with "Other" at the end
        const sortedCategories = [...categoriesData].sort((a, b) => {
          if ((a.name || '').toLowerCase() === 'other') return 1;
          if ((b.name || '').toLowerCase() === 'other') return -1;
          return (a.name || '').localeCompare(b.name || '');
        });
        setCategories(sortedCategories);
        // Sort cities alphabetically by name
        const sortedCities = [...citiesData].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '')
        );
        setCities(sortedCities);
      } catch (error) {
        console.error('Error initializing app:', error);
        setUser({ isAuthenticated: false });
      } finally {
        setIsLoading(false);
      }
    };

    initializeApp();
  }, [checkAuthStatus]);

  const value = {
    user,
    setUser,
    categories,
    cities,
    isLoading,
    setIsLoading,
    login,
    logout,
    verify2FA,
    authFetch,
    TokenService,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
export { authFetch, TokenService };
